import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Layers, Clock, ListChecks, GraduationCap, CalendarDays, Search, Archive } from "lucide-react";
import { Layout } from "../../components/layout/Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Field, EmptyState } from "../../components/common";
import { DurationInput } from "../../components/DurationInput";
import { api, formatApiError } from "../../lib/api";
import { fmtDuration } from "../../lib/format";
import { useAuth, hasPerm } from "../../context/AuthContext";

export function ModuleFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [nom, setNom] = useState("");
  const [description, setDescription] = useState("");
  const [duree, setDuree] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setNom(initial?.nom || "");
    setDescription(initial?.description || "");
    setDuree(initial?.duree_manuelle ?? null);
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { nom, description, duree_minutes: duree };
      const { data } = initial ? await api.patch(`/modules/${initial.id}`, payload) : await api.post("/modules", payload);
      toast.success(initial ? "Module modifié" : "Module créé");
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? "Modifier le module" : "Créer un module"}</DialogTitle>
          <DialogDescription>Vous ajouterez ensuite les activités du module.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="module-form">
          <Field label="Nom du module" required>
            <Input value={nom} onChange={(e) => setNom(e.target.value)} required autoFocus placeholder="Remplacement écran iPhone" data-testid="module-form-nom" />
          </Field>
          <Field label="Description">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Objectifs, matériel nécessaire..." data-testid="module-form-description" />
          </Field>
          <Field
            label="Durée du module (facultatif)"
            hint={initial?.duree_auto ? `Actuellement calculée automatiquement : ${fmtDuration(initial.duree_minutes)} (somme des activités). Cette valeur ne sert que si aucune activité n'a de durée.` : "Dès qu'une activité a une durée, la durée du module devient la somme des durées des activités."}
          >
            <DurationInput value={duree} onChange={setDuree} idPrefix="module-form-duree" />
          </Field>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="module-form-submit">{initial ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ModuleListPage() {
  const { user } = useAuth();
  const canManage = hasPerm(user, "modules", "manage");
  const navigate = useNavigate();
  const [modules, setModules] = useState(null);
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => api.get("/modules", { params: { include_archived: archived } }).then(({ data }) => setModules(data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [archived]);

  const filtered = (modules || []).filter((m) => `${m.nom} ${m.description}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <Layout
      title="Modules"
      actions={canManage && <Button onClick={() => setCreateOpen(true)} data-testid="module-create-button"><Plus size={16} /> <span className="hidden sm:inline">Créer un module</span></Button>}
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un module..." className="pl-9" />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground"><Checkbox checked={archived} onCheckedChange={(v) => setArchived(!!v)} /> Afficher les modules archivés</label>
      </div>

      {modules === null ? (
        <p className="text-muted-foreground">Chargement...</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border bg-card">
          <EmptyState
            icon={Layers}
            title={modules.length ? "Aucun module ne correspond" : "Aucun module pour l'instant"}
            text={modules.length ? undefined : "Créez vos modules de formation (ex. « Remplacement écran iPhone ») puis leurs activités."}
            action={canManage && !modules.length ? <Button onClick={() => setCreateOpen(true)}><Plus size={16} /> Créer un module</Button> : null}
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((m) => (
            <button
              key={m.id}
              onClick={() => navigate(`/modules/${m.id}`)}
              className="text-left rounded-xl border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all flex flex-col"
              data-testid={`module-card-${m.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading font-semibold leading-snug">{m.nom}</h3>
                {m.archived && <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"><Archive size={11} /> Archivé</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2 flex-1">{m.description || "Pas de description"}</p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Clock size={13} />{fmtDuration(m.duree_minutes)}{m.duree_auto ? " (auto)" : ""}</span>
                <span className="flex items-center gap-1"><ListChecks size={13} />{m.activities.length} activité{m.activities.length > 1 ? "s" : ""}</span>
                <span className="flex items-center gap-1"><GraduationCap size={13} />{m.nb_eleves} élève{m.nb_eleves > 1 ? "s" : ""}</span>
                <span className="flex items-center gap-1"><CalendarDays size={13} />{m.nb_cours} cours</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <ModuleFormDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={(m) => navigate(`/modules/${m.id}`)} />
    </Layout>
  );
}
